import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { MessageSquare, Star, Send } from "lucide-react";
import { toast } from "sonner";
import { submitFeedback } from "../lib/api";

/**
 * Frictionless, anonymous floating feedback button. Mounted globally in App.js.
 * - No email required, no signup.
 * - Stars optional (nice-to-have, not enforced).
 * - Hidden on /report because ReportPage already mounts a contextual FeedbackWidget.
 */
export default function FloatingFeedback() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Avoid double-widgets on the report page (it has its own contextual widget).
  const hideOnPath = location.pathname.startsWith("/report");

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setRating(0);
      setHover(0);
      setComment("");
    }
  }, [open]);

  // Allow any page/component to programmatically open the widget by dispatching
  // a `cfograde:open-feedback` custom event on the window. Used by every
  // "contact us" / "send feedback" CTA across the app — keeps us inbox-free.
  useEffect(() => {
    const handler = () => {
      if (!hideOnPath) setOpen(true);
    };
    window.addEventListener("cfograde:open-feedback", handler);
    return () => window.removeEventListener("cfograde:open-feedback", handler);
  }, [hideOnPath]);

  if (hideOnPath) return null;

  async function handleSubmit() {
    const text = comment.trim();
    if (!text && rating === 0) {
      toast.error("Tell us anything — a word, a wish, a bug.");
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback({
        rating: rating > 0 ? rating : null,
        comment: text,
        email: "",
        audience: "",
        industry: "",
        health_score: null,
        rating_label: "",
        page: location.pathname || "global",
      });
      toast.success("Got it — thank you. We read every note.");
      setOpen(false);
    } catch {
      toast.error("Couldn't send that. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="floating-feedback-btn"
        aria-label="Send feedback"
        className="no-print fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 flex items-center gap-2 bg-[#1F382E] text-[#F7F5F0] rounded-full pl-3.5 pr-4 py-2.5 shadow-lg hover:bg-[#142922] hover:translate-y-[-1px] transition-all duration-300"
      >
        <MessageSquare size={15} strokeWidth={1.5} />
        <span className="text-[13px] tracking-wide">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#E5E0D8] sm:max-w-md"
          data-testid="floating-feedback-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#1F382E]">
              What's on your mind?
            </DialogTitle>
            <DialogDescription className="text-[#5C665D]">
              No email, no signup. Just type — it lands straight in our inbox.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="A bug, an idea, a missing metric, a kind word — anything goes."
              data-testid="floating-feedback-comment"
              autoFocus
              className="bg-white border-[#E5E0D8] focus-visible:ring-[#1F382E] min-h-[120px]"
            />

            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-[#5C665D] mb-1.5">
                Optional — how are we doing?
              </p>
              <div
                className="flex items-center gap-1"
                data-testid="floating-feedback-rating"
                onMouseLeave={() => setHover(0)}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(rating === n ? 0 : n)}
                    onMouseEnter={() => setHover(n)}
                    data-testid={`floating-feedback-star-${n}`}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  >
                    <Star
                      size={22}
                      strokeWidth={1.5}
                      className={
                        (hover || rating) >= n
                          ? "fill-[#C26D5C] text-[#C26D5C]"
                          : "text-[#D4C9B6]"
                      }
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="floating-feedback-submit-btn"
              className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md transition-all duration-300"
            >
              <Send size={14} strokeWidth={1.5} className="mr-2" />
              {submitting ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
